import { useEffect } from 'react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyModal({ isOpen, onClose }: PrivacyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Informativa sul trattamento dei dati personali</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        <div className="overflow-y-auto px-8 py-6 text-sm text-gray-700 leading-relaxed space-y-5">
          <p className="text-xs text-gray-500 italic">ai sensi dell&apos;art. 13 del Reg. (UE) n. 2016/679</p>

          <p>
            Ai sensi dell&apos;art. 13 del Regolamento (UE) n. 2016/679 (di seguito il &quot;GDPR&quot;), <strong>Global Energy S.r.l.</strong> (di seguito il &quot;Titolare&quot;) La informa che i Suoi dati personali (di seguito i &quot;Dati&quot;), raccolti presso di Lei o presso terzi, saranno trattati nel rispetto di quanto previsto dal GDPR e in conformità all&apos;informativa che segue.
          </p>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Titolare del trattamento. Responsabili del trattamento.</h3>
            <p>
              Il Titolare del trattamento dei Dati è <strong>Global Energy S.r.l.</strong> con sede in Via Don Luigi Sturzo 181, 52100 Arezzo (AR); email: info.globalenergysrl@gmail.com. L&apos;elenco aggiornato degli eventuali responsabili del trattamento è disponibile presso la sede del Titolare.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Responsabile protezione dei dati.</h3>
            <p>
              Il Responsabile della protezione dei dati è contattabile tramite i recapiti indicati sopra.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Finalità e base giuridica del trattamento. Legittimi interessi perseguiti.</h3>
            <p>
              I Dati saranno trattati per rispondere alle richieste di contatto, preventivo e informazioni inviate tramite il modulo presente sul sito, nonché per l&apos;esecuzione di eventuali contratti e per adempiere agli obblighi di legge.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Destinatari o categorie di destinatari.</h3>
            <p>
              I Dati potranno essere resi accessibili, portati a conoscenza di o comunicati ai seguenti soggetti, i quali saranno nominati dal Titolare, a seconda dei casi, quali Responsabili o agiranno in qualità di titolari autonomi: collaboratori e dipendenti del Titolare, fornitori di servizi informatici e di comunicazione. In ogni caso, i Dati non saranno diffusi.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Trasferimento dei Dati a paesi terzi.</h3>
            <p>
              I dati circoleranno nell&apos;ambito di paesi appartenenti all&apos;UE. I Dati, inoltre, potranno essere comunicati alle società controllate dal Titolare, ovvero dalle società affiliate al Titolare, situate in paesi non appartenenti all&apos;Unione Europea, occasionalmente ed esclusivamente nel caso in cui il trasferimento sia necessario all&apos;esecuzione di un contratto concluso con l&apos;interessato e il titolare del trattamento, ovvero all&apos;esecuzione di misure precontrattuali adottate su istanza dell&apos;interessato (art. 49, comma 2, lett. b)).
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Periodo di conservazione.</h3>
            <p>
              I dati saranno conservati per un periodo di tempo massimo pari al periodo di prescrizione dei diritti azionabili dal Titolare, come di volta in volta applicabile.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Diritti di accesso, cancellazione, limitazione e portabilità.</h3>
            <p className="mb-2">
              Agli interessati sono riconosciuti i diritti di cui agli artt. da 15 a 20 del GDPR. A titolo esemplificativo, ciascun interessato potrà:
            </p>
            <ul className="list-none space-y-1 pl-2">
              <li>a) ottenere la conferma che sia o meno in corso un trattamento di dati personali che lo riguardano;</li>
              <li>b) qualora un trattamento sia in corso, ottenere l&apos;accesso ai dati personali e alle informazioni relative al trattamento nonché richiedere una copia dei dati personali;</li>
              <li>c) ottenere la rettifica dei dati personali inesatti e l&apos;integrazione dei dati personali incompleti;</li>
              <li>d) ottenere, qualora sussista una delle condizioni previste dall&apos;art. 17 del GDPR, la cancellazione dei dati personali che lo riguardano;</li>
              <li>e) ottenere, nei casi previsti dall&apos;art. 18 del GDPR, la limitazione del trattamento;</li>
              <li>f) ricevere i dati personali che lo riguardano in un formato strutturato, di uso comune e leggibile da dispositivo automatico e richiedere la loro trasmissione ad un altro titolare, se tecnicamente fattibile.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Diritto di opposizione.</h3>
            <p>
              Ciascun interessato ha il diritto di opporsi in qualsiasi momento al trattamento dei suoi dati personali effettuato per il perseguimento di un legittimo interesse del Titolare. In caso di opposizione, i suoi dati personali non saranno più oggetto di trattamento, sempre che non sussistano motivi legittimi per procedere al trattamento che prevalgono sugli interessi, sui diritti e sulle libertà dell&apos;interessato oppure per l&apos;accertamento, l&apos;esercizio o la difesa di un diritto in sede giudiziaria.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Diritto di revoca del consenso.</h3>
            <p>
              Nel caso in cui sia richiesto il consenso per il trattamento dei dati personali, ciascun interessato potrà, altresì, revocare in qualsiasi momento il consenso già prestato, senza pregiudicare la liceità del trattamento basata sul consenso prestato prima della revoca.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Diritto di proporre reclamo al Garante.</h3>
            <p>
              Inoltre ciascun interessato potrà proporre reclamo al Garante per la Protezione dei Dati Personali nel caso in cui ritenga che siano stati violati i diritti di cui è titolare ai sensi del GDPR, secondo le modalità indicate sul sito internet del Garante accessibile all&apos;indirizzo:{' '}
              <a
                href="https://www.garanteprivacy.it"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-emerald-600 hover:underline"
              >
                www.garanteprivacy.it
              </a>.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Diritti dell&apos;interessato.</h3>
            <p>
              I diritti dell&apos;interessato potranno essere esercitati tramite i recapiti di riferimento sopra indicati.
            </p>
          </div>
        </div>

        <div className="px-8 py-5 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Ho letto e compreso l&apos;informativa
          </button>
        </div>
      </div>
    </div>
  );
}
